import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabaseUrl } from '../lib/environments';

interface InviteData {
  inviter_name: string;
  inviter_avatar: string | null;
  email: string | null;
  phone: string | null;
  context_type: string | null;
}

export const InvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!token) {
      setExpired(true);
      setLoading(false);
      return;
    }

    const fetchInvite = async () => {
      try {
        const supabaseUrl = getSupabaseUrl();
        const res = await fetch(
          `${supabaseUrl}/functions/v1/send-link-invite?token=${token}`
        );
        if (!res.ok) {
          setExpired(true);
          setLoading(false);
          return;
        }
        const json = await res.json();
        setData(json);
      } catch {
        setExpired(true);
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [token]);

  const handleAccept = () => {
    const params = new URLSearchParams();
    if (token) params.set('invite_token', token);
    if (data?.email) params.set('email', data.email);
    navigate(`/signup?${params.toString()}`);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (expired || !data) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>Invitation Expired</h1>
          <p style={styles.description}>
            This invitation is no longer valid. Ask your friend to send a new one.
          </p>
          <a href="https://kizu.online" style={styles.button}>
            Visit Kizu
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {data.inviter_avatar ? (
          <img
            src={data.inviter_avatar}
            alt={data.inviter_name}
            style={styles.avatar}
          />
        ) : (
          <div style={styles.avatarPlaceholder}>
            {data.inviter_name.charAt(0)}
          </div>
        )}
        <h1 style={styles.title}>
          {data.inviter_name} invited you to Kizu
        </h1>
        <p style={styles.description}>
          Where your digital memories live.
        </p>
        <button onClick={handleAccept} style={styles.button}>
          Accept Invitation
        </button>
        <p style={styles.logo}>Kizu — Photos with the people you care about</p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    background: '#0a0a0a',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    textAlign: 'center' as const,
    maxWidth: 400,
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    objectFit: 'cover' as const,
    marginBottom: 20,
    border: '3px solid #333',
  },
  avatarPlaceholder: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    background: '#222',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 36,
    color: '#fff',
    marginBottom: 20,
    border: '3px solid #333',
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: '#fff',
    marginBottom: 8,
  },
  description: {
    fontSize: 16,
    color: '#999',
    marginBottom: 32,
    lineHeight: 1.5,
  },
  button: {
    display: 'inline-block',
    background: '#4A90D9',
    color: '#fff',
    padding: '14px 40px',
    borderRadius: 12,
    textDecoration: 'none',
    fontSize: 16,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
  },
  logo: {
    fontSize: 14,
    color: '#555',
    marginTop: 32,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #333',
    borderTopColor: '#4A90D9',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  loadingText: {
    color: '#999',
    fontSize: 16,
  },
};
